import { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Loader2, Plus, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { KbEntry, KbSourceType, CreateEntryRequest, UpdateEntryRequest } from '@/hooks/useKnowledgeBase';
import { SELECTABLE_SOURCE_TYPES } from '../kbDisplay';
import { DuplicateWarning } from './DuplicateWarning';

export interface KbEntryFormProps {
  entry?: KbEntry | null;
  categories: string[];
  onSubmit: (data: CreateEntryRequest | UpdateEntryRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  duplicateCheck?: {
    isChecking: boolean;
    result: {
      is_duplicate: boolean;
      similar_entries: Array<{
        id: number;
        question_text: string;
        similarity_score: number;
      }>;
    } | null;
  };
  onQuestionChange?: (text: string) => void;
  className?: string;
}

interface FormErrors {
  question_text?: string;
  response_text?: string;
  category?: string;
}

export function KbEntryForm({
  entry,
  categories,
  onSubmit,
  onCancel,
  isSubmitting = false,
  duplicateCheck,
  onQuestionChange,
  className,
}: KbEntryFormProps) {
  const isEditMode = !!entry;

  // Form state
  const [questionText, setQuestionText] = useState(entry?.question_text || '');
  const [responseText, setResponseText] = useState(entry?.response_text || '');
  const [sourceType, setSourceType] = useState<KbSourceType>(entry?.source_type || 'Qualitative');
  const [category, setCategory] = useState(entry?.category || '');
  const [newCategory, setNewCategory] = useState('');
  const [tags, setTags] = useState<string[]>(entry?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [firmId, setFirmId] = useState<string>(entry?.firm_id?.toString() || '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Debounced duplicate check
  useEffect(() => {
    if (!isEditMode && onQuestionChange && questionText.length > 10) {
      const timer = setTimeout(() => {
        onQuestionChange(questionText);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [questionText, isEditMode, onQuestionChange]);

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!questionText.trim()) {
      newErrors.question_text = 'Question text is required';
    } else if (questionText.trim().length < 10) {
      newErrors.question_text = 'Question must be at least 10 characters';
    }

    if (!responseText.trim()) {
      newErrors.response_text = 'Response text is required';
    } else if (responseText.trim().length < 10) {
      newErrors.response_text = 'Response must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [questionText, responseText]);

  // Handle field blur for validation
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validate();
  };

  // Handle tag addition
  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Handle category creation
  const handleAddCategory = () => {
    if (newCategory.trim()) {
      setCategory(newCategory.trim());
      setNewCategory('');
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      // Mark all fields as touched to show errors
      setTouched({
        question_text: true,
        response_text: true,
        category: true,
      });
      return;
    }

    const data: CreateEntryRequest | UpdateEntryRequest = {
      question_text: questionText.trim(),
      response_text: responseText.trim(),
      category: category || null,
      tags,
    };

    if (!isEditMode) {
      (data as CreateEntryRequest).source_type = sourceType;
      (data as CreateEntryRequest).firm_id = firmId ? parseInt(firmId, 10) : null;
    }

    onSubmit(data);
  };

  return (
    <Card className={cn('max-w-3xl', className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle>{isEditMode ? 'Edit Entry' : 'Create New Entry'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Question Text */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Question <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              onBlur={() => handleBlur('question_text')}
              placeholder="Enter the question text..."
              rows={3}
              className={cn(
                touched.question_text && errors.question_text && 'border-red-500'
              )}
            />
            {touched.question_text && errors.question_text && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.question_text}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {questionText.length} characters
            </p>
          </div>

          {/* Duplicate Warning */}
          {!isEditMode && duplicateCheck && (
            <DuplicateWarning
              isChecking={duplicateCheck.isChecking}
              result={duplicateCheck.result}
            />
          )}

          {/* Response Text */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Response <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              onBlur={() => handleBlur('response_text')}
              placeholder="Enter the response text..."
              rows={6}
              className={cn(
                touched.response_text && errors.response_text && 'border-red-500'
              )}
            />
            {touched.response_text && errors.response_text && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.response_text}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {responseText.length} characters
            </p>
          </div>

          {/* Source Type (only for create) */}
          {!isEditMode && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Source Type</label>
              <div className="flex flex-wrap gap-2">
                {SELECTABLE_SOURCE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSourceType(type.value)}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-md border transition-colors',
                      sourceType === type.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted border-input'
                    )}
                    title={type.description}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Category</label>
            <div className="flex gap-2">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex-1 text-sm border rounded-md px-3 py-2 bg-background"
              >
                <option value="">Select a category...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <div className="flex gap-1">
                <Input
                  type="text"
                  placeholder="New category..."
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-40"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCategory}
                  disabled={!newCategory.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Tags</label>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Add a tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
              >
                <Tag className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Firm ID (only for create) */}
          {!isEditMode && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Firm ID (Optional)</label>
              <Input
                type="number"
                placeholder="Leave empty for global entry"
                value={firmId}
                onChange={(e) => setFirmId(e.target.value)}
                className="w-40"
              />
              <p className="text-xs text-muted-foreground">
                If specified, this entry will only be available for that firm.
              </p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditMode ? 'Update Entry' : 'Create Entry'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
